"use client";
import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ResetSchema } from "@/schemas";
import { z } from "zod";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function ForgotPasswordForm() {
  type ForgotPasswordValues = z.infer<typeof ResetSchema>;
  const form = useForm<ForgotPasswordValues>({
    resolver: zodResolver(ResetSchema),
    defaultValues: { email: "" },
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [smsMode, setSmsMode] = React.useState(false);
  const [smsSent, setSmsSent] = React.useState(false);
  const [smsCode, setSmsCode] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");

  const onSubmit = async (values: ForgotPasswordValues) => {
    setIsSubmitting(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      if (!res.ok) {
        const data = await res.json();
        toast.error(data.error || "Failed to send reset link");
      } else {
        toast.success(
          "If that email is registered, a password reset link has been sent."
        );
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to send reset link");
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendSms = async () => {
    const email = form.getValues("email");
    if (!email) { toast.error("Enter your email first"); return; }
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/auth/forgot-password/sms', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to send code');
      setSmsSent(true);
      toast.success('If a phone is on file, a code was sent by SMS.');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to send code');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetWithSms = async () => {
    const email = form.getValues("email");
    if (!email) { toast.error("Enter your email"); return; }
    if (!smsCode || !newPassword) { toast.error('Enter code and new password'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/auth/reset-password-sms', { method: 'POST', headers: { 'Content-Type':'application/json' }, body: JSON.stringify({ email, code: smsCode, password: newPassword, confirmPassword }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to reset');
      toast.success('Password updated. You can now sign in.');
      window.location.href = '/auth/login';
    } catch (e: any) {
      toast.error(e?.message || 'Failed to reset');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  disabled={isSubmitting}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="grid grid-cols-1 gap-2">
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "Sending reset link..." : "Send reset link"}
          </Button>
          <button type="button" onClick={()=> setSmsMode((v)=>!v)} className="w-full text-xs underline opacity-80">
            {smsMode ? 'Use email reset instead' : 'Prefer SMS? Reset via text message'}
          </button>
        </div>

        {smsMode && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" disabled={isSubmitting} onClick={sendSms}>
                {smsSent ? 'Resend code' : 'Send code to phone'}
              </Button>
              <input value={smsCode} onChange={(e)=> setSmsCode(e.target.value)} placeholder="Enter code" className="flex-1 px-2 py-2 rounded border" />
            </div>
            <div className="grid grid-cols-1 gap-2">
              <input type="password" value={newPassword} onChange={(e)=> setNewPassword(e.target.value)} placeholder="New password" className="w-full px-2 py-2 rounded border" />
              <input type="password" value={confirmPassword} onChange={(e)=> setConfirmPassword(e.target.value)} placeholder="Confirm new password" className="w-full px-2 py-2 rounded border" />
              <Button type="button" onClick={resetWithSms} disabled={isSubmitting} className="w-full">Reset password</Button>
            </div>
            <div className="text-xs opacity-70">Limit: 2 SMS reset codes per day.</div>
          </div>
        )}
      </form>
    </Form>
  );
}